/**
 * Drywall Repair and Replacement Calculator — public calculator page
 * (PRP-CALC-1 S3, reworked per founder gate). Produces a real-Xactimate
 * line-item scope for a room's drywall work: remove + hang board, texture,
 * seal/paint, baseboard, door trim, and water tear-out — quantities and codes
 * only, no pricing. Xactimate prices the items at import.
 */
import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import { Label } from '../../components/ui/Label';
import ToolPageShell, { DimensionDivider, Eyebrow } from './ToolPageShell';
import AppPromo from './AppPromo';
import CalcResults from './CalcResults';
import {
  MethodSection,
  FaqSection,
  SourcesSection,
  CheckboxField,
  NumberField,
  SelectField,
  type FaqItem,
} from './_shared';
import { useSeo, faqJsonLd, calculatorJsonLd } from './useSeo';
import {
  CalcAPI,
  type CalcResponse,
  type DrywallScope,
  type DrywallThickness,
  type DrywallTexture,
  type DrywallActivity,
  type DrywallWaterDamage,
  type DrywallFloodCut,
  type BaseboardAction,
  type BaseboardSize,
  type DrywallOpening,
  type OpeningTrimAction,
} from './calcApi';

interface OpeningRow extends DrywallOpening {
  id: number;
}

let nextOpeningId = 1;

function newOpening(): OpeningRow {
  return {
    id: nextOpeningId++,
    width_ft: 3,
    height_ft: 6.75,
    kind: 'door',
    subtract_from_wall: true,
    trim_action: 'replace',
  };
}

const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'How much drywall do I need for a room?',
    a: 'Take the room perimeter (twice the length plus twice the width) and multiply by the wall height to get gross wall square footage, then subtract the area of every door and window you are not hanging board across. Add the ceiling as length times width if it is in the scope. Divide the finished surface by 32 square feet per 4x8 sheet and add about 10 percent for waste and cuts. A 14 by 12 room with 8 foot walls is a 52 foot perimeter, so 416 square feet of wall before openings — roughly 13 sheets, or about 14 with waste. The calculator does this and subtracts the openings you mark.',
  },
  {
    q: 'How does a 2 ft or 4 ft flood cut change the quantities?',
    a: 'A flood cut only tears out and replaces the wet band of drywall, not the full wall. Pick 2 ft or 4 ft and the tool bills the tear-out per linear foot of wall at that height and cuts the replacement board to the same height, so a 52 foot perimeter at a 2 ft cut is about 104 square feet of board instead of the full 416. The upper wall stays, and texture and paint still run the full wall so the repair blends.',
  },
  {
    q: 'How do I match existing texture?',
    a: 'Match the method that is already on the wall: light or heavy hand texture, machine orange peel, machine knockdown, or a smooth skim coat. Pick the closest one and the tool figures it on the full finished surface, not just the patch, because a texture that stops mid-wall reads as a repair. On a patch, texturing the whole wall to a natural break is usually what makes it disappear.',
  },
  {
    q: 'How much paint will the room take?',
    a: 'One gallon covers about 350 to 400 square feet per coat on primed drywall. New or bare board needs a seal coat first, then your finish coats. Set 1 or 2 coats and the tool figures paint on the full finished surface times the coats. A 416 square foot wall at 2 coats is about 832 square feet of coverage, so roughly 2 to 3 gallons plus the seal coat.',
  },
  {
    q: 'When should I paint the whole wall instead of touching up?',
    a: 'Paint to a natural break — a corner, not a spot in the field of the wall. Sheen and age make a touch-up flash, so once a repair is more than a small patch you paint corner to corner, and if two walls meet with no trim between them you often carry it around. The tool figures paint on the full wall for that reason.',
  },
  {
    q: 'How much baseboard do I need?',
    a: 'Baseboard runs by the linear foot along the room perimeter, less the door openings. Detach and reset keeps the existing base and puts it back; remove and replace installs new base at the size you pick; leave in place masks and preps it per linear foot so it can stay while the wall is painted. The tool measures the run from the room dimensions.',
  },
  {
    q: 'Can I use these quantities in Xactimate?',
    a: 'Yes. Every line resolves to a real Xactimate item — DRY for board, texture, and tear-out, PNT for seal and paint, FNC for baseboard and trim — so the quantities map straight to line items and Xactimate prices them against your loaded price list at import.',
  },
  {
    q: 'Why is there no price on the line items?',
    a: 'Because Xactimate prices them at import against your price list, and publishing a rate here would be inventing a number. The tool gives you the defensible codes, quantities, and actions so the scope holds up, and the pricing comes from your list.',
  },
];

export default function DrywallCalculatorPage() {
  useSeo({
    title:
      'Drywall Repair Calculator — Sheets, Mud, Tape & Paint Quantities | Antero Trail',
    description:
      'Free drywall repair calculator. Enter room dimensions and get drywall sheets, joint compound, tape, texture, paint, and baseboard quantities for repair and flood-cut estimates.',
    path: '/tools/drywall-repair-calculator',
    jsonLd: [
      calculatorJsonLd({
        name: 'Drywall Repair Calculator',
        description:
          'Turns room dimensions into drywall board, joint compound, tape, texture, paint, and baseboard quantities as real Xactimate line items for repair and water-loss estimates.',
        path: '/tools/drywall-repair-calculator',
      }),
      faqJsonLd(FAQ_ITEMS),
    ],
  });

  const [length, setLength] = useState<number>(14);
  const [width, setWidth] = useState<number>(12);
  const [height, setHeight] = useState<number>(8);
  const [openings, setOpenings] = useState<OpeningRow[]>([newOpening()]);
  const [scope, setScope] = useState<DrywallScope>('walls');
  const [thickness, setThickness] = useState<DrywallThickness>('1/2');
  const [texture, setTexture] = useState<DrywallTexture>('machine');
  const [activity, setActivity] = useState<DrywallActivity>('replace');
  const [patchCount, setPatchCount] = useState<number>(1);
  const [paintCoats, setPaintCoats] = useState<1 | 2>(2);
  const [waterDamage, setWaterDamage] = useState<DrywallWaterDamage>('none');
  const [floodCut, setFloodCut] = useState<DrywallFloodCut>('none');
  const [baseboardAction, setBaseboardAction] =
    useState<BaseboardAction>('detach_reset');
  const [baseboardSize, setBaseboardSize] = useState<BaseboardSize>('3_1/4');
  const [floorProtection, setFloorProtection] = useState<boolean>(true);

  const [result, setResult] = useState<CalcResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Flood cut only applies to wall work with a water condition selected.
  const showFloodCut = waterDamage !== 'none' && scope !== 'ceiling';

  function addOpening() {
    setOpenings((rows) => [...rows, newOpening()]);
  }
  function removeOpening(id: number) {
    setOpenings((rows) => rows.filter((r) => r.id !== id));
  }
  function updateOpening(id: number, patch: Partial<OpeningRow>) {
    setOpenings((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await CalcAPI.drywallRoom({
        length_ft: length,
        width_ft: width,
        height_ft: height,
        openings: openings.map(
          ({ width_ft, height_ft, kind, subtract_from_wall, trim_action }) => ({
            width_ft,
            height_ft,
            kind,
            subtract_from_wall,
            // Trim action only carries for doors; windows send "none".
            trim_action: kind === 'door' ? trim_action : 'none',
          }),
        ),
        scope,
        thickness,
        texture,
        activity,
        ...(activity === 'patch' ? { patch_count: patchCount } : {}),
        paint_coats: paintCoats,
        water_damage: waterDamage,
        // Backend rejects a flood cut on ceiling-only or no-water scopes.
        flood_cut: showFloodCut ? floodCut : 'none',
        baseboard_action: baseboardAction,
        baseboard_size: baseboardSize,
        floor_protection: floorProtection,
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <ToolPageShell>
      <Eyebrow>Interior — free calculator</Eyebrow>
      <h1 className="mt-2 font-ui text-4xl font-bold tracking-tight sm:text-[2.75rem] sm:leading-tight">
        Drywall repair calculator
      </h1>
      <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
        Turn room dimensions into a drywall repair scope: board and sheet
        count, joint compound and tape, texture, seal and paint, and baseboard.
        Enter the room, subtract the doors and windows, choose the work, and the
        tool returns real Xactimate line items with quantities and actions.
        Xactimate prices them at import, so no rates are shown here.
      </p>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-lg border border-border bg-card p-5 sm:p-6"
          aria-label="Drywall inputs"
        >
          <div className="grid grid-cols-3 gap-3">
            <NumberField
              label="Length"
              suffix="ft"
              value={length}
              min={1}
              onChange={setLength}
            />
            <NumberField
              label="Width"
              suffix="ft"
              value={width}
              min={1}
              onChange={setWidth}
            />
            <NumberField
              label="Height"
              suffix="ft"
              value={height}
              min={1}
              onChange={setHeight}
            />
          </div>

          <SelectField<DrywallScope>
            label="Scope"
            value={scope}
            onChange={setScope}
            options={[
              { value: 'walls', label: 'Walls only' },
              { value: 'ceiling', label: 'Ceiling only' },
              { value: 'both', label: 'Walls and ceiling' },
            ]}
          />
          <SelectField<DrywallActivity>
            label="Work"
            value={activity}
            onChange={setActivity}
            options={[
              { value: 'replace', label: 'Replace (remove and hang new)' },
              { value: 'patch', label: 'Patch a section' },
            ]}
          />
          {activity === 'patch' && (
            <NumberField
              label="Patch locations"
              value={patchCount}
              min={1}
              step="1"
              onChange={setPatchCount}
              hint="How many separate patch spots to repair and blend."
            />
          )}
          <div className="grid grid-cols-2 gap-3">
            <SelectField<DrywallThickness>
              label="Board thickness"
              value={thickness}
              onChange={setThickness}
              options={[
                { value: '1/2', label: '1/2 in' },
                { value: '5/8', label: '5/8 in' },
              ]}
            />
            <SelectField<number>
              label="Paint coats"
              value={paintCoats}
              onChange={(v) => setPaintCoats(Number(v) as 1 | 2)}
              options={[
                { value: 1, label: '1 coat' },
                { value: 2, label: '2 coats' },
              ]}
            />
          </div>
          <SelectField<DrywallTexture>
            label="Texture"
            value={texture}
            onChange={setTexture}
            options={[
              { value: 'none', label: 'None' },
              { value: 'light_hand', label: 'Light hand texture' },
              { value: 'heavy_hand', label: 'Heavy hand texture' },
              { value: 'machine', label: 'Machine (orange peel)' },
              { value: 'machine_knockdown', label: 'Machine knockdown' },
              { value: 'smooth_skim', label: 'Smooth wall / skim coat' },
            ]}
          />

          {/* Water damage */}
          <div className="space-y-4 border-t border-border pt-3">
            <SelectField<DrywallWaterDamage>
              label="Water damage"
              value={waterDamage}
              onChange={(v) => {
                setWaterDamage(v);
                if (v === 'none') setFloodCut('none');
              }}
              options={[
                { value: 'none', label: 'No water damage' },
                { value: 'clean', label: 'Water - clean/gray (Cat 1-2)' },
                { value: 'cat3', label: 'Water - Cat 3 (sewage/outside water)' },
                {
                  value: 'flood_nfip',
                  label: 'Flood (NFIP claim - no-bag tear out)',
                },
              ]}
            />
            {showFloodCut && (
              <SelectField<DrywallFloodCut>
                label="Tear-out height"
                value={floodCut}
                onChange={setFloodCut}
                options={[
                  { value: 'none', label: 'Full height' },
                  { value: '2ft', label: '2 ft flood cut' },
                  { value: '4ft', label: '4 ft flood cut' },
                ]}
              />
            )}
          </div>

          {/* Baseboard */}
          <fieldset className="space-y-2 border-t border-border pt-3">
            <legend className="text-sm font-medium text-foreground">
              Baseboard
            </legend>
            <div className="space-y-1.5">
              {(
                [
                  ['detach_reset', 'Detach & reset'],
                  ['remove_replace', 'Remove & replace'],
                  ['none', 'Leave in place (mask per LF)'],
                ] as [BaseboardAction, string][]
              ).map(([val, label]) => (
                <label
                  key={val}
                  className="flex cursor-pointer items-center gap-2 text-sm text-foreground"
                >
                  <input
                    type="radio"
                    name="baseboard-action"
                    value={val}
                    checked={baseboardAction === val}
                    onChange={() => setBaseboardAction(val)}
                    className="h-4 w-4 border-input"
                  />
                  {label}
                </label>
              ))}
            </div>
            {baseboardAction === 'remove_replace' && (
              <SelectField<BaseboardSize>
                label="Baseboard size"
                value={baseboardSize}
                onChange={setBaseboardSize}
                options={[
                  { value: '2_1/4', label: '2 1/4 in' },
                  { value: '3_1/4', label: '3 1/4 in' },
                  { value: '5_1/4', label: '5 1/4 in' },
                ]}
              />
            )}
          </fieldset>

          <div className="border-t border-border pt-3">
            <CheckboxField
              label="Floor protection (heavy paper and tape)"
              checked={floorProtection}
              onChange={setFloorProtection}
            />
          </div>

          {/* Openings: dynamic add/remove rows */}
          <div className="space-y-3 border-t border-border pt-3">
            <div className="flex items-center justify-between">
              <Label>Openings (doors and windows)</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addOpening}
                Icon={Plus}
              >
                Add opening
              </Button>
            </div>
            {openings.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No openings. The whole wall area is counted.
              </p>
            )}
            {openings.map((o, i) => (
              <div
                key={o.id}
                className="space-y-2 rounded-md border border-border p-3"
              >
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label
                      htmlFor={`op-kind-${o.id}`}
                      className="text-xs text-muted-foreground"
                    >
                      Type
                    </Label>
                    <select
                      id={`op-kind-${o.id}`}
                      aria-label={`Opening ${i + 1} type`}
                      value={o.kind}
                      onChange={(e) =>
                        updateOpening(o.id, {
                          kind: e.target.value as DrywallOpening['kind'],
                        })
                      }
                      className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="door">Door</option>
                      <option value="window">Window</option>
                    </select>
                  </div>
                  <div className="w-20">
                    <Label
                      htmlFor={`op-w-${o.id}`}
                      className="text-xs text-muted-foreground"
                    >
                      W (ft)
                    </Label>
                    <input
                      id={`op-w-${o.id}`}
                      aria-label={`Opening ${i + 1} width`}
                      type="number"
                      min={0}
                      step="any"
                      value={o.width_ft}
                      onChange={(e) =>
                        updateOpening(o.id, {
                          width_ft: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                  <div className="w-20">
                    <Label
                      htmlFor={`op-h-${o.id}`}
                      className="text-xs text-muted-foreground"
                    >
                      H (ft)
                    </Label>
                    <input
                      id={`op-h-${o.id}`}
                      aria-label={`Opening ${i + 1} height`}
                      type="number"
                      min={0}
                      step="any"
                      value={o.height_ft}
                      onChange={(e) =>
                        updateOpening(o.id, {
                          height_ft: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeOpening(o.id)}
                    aria-label={`Remove opening ${i + 1}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-xs text-foreground">
                  <input
                    type="checkbox"
                    aria-label={`Opening ${i + 1} subtract from wall`}
                    checked={o.subtract_from_wall}
                    onChange={(e) =>
                      updateOpening(o.id, { subtract_from_wall: e.target.checked })
                    }
                    className="h-4 w-4 rounded border-input"
                  />
                  Subtract from wall area
                </label>
                {o.kind === 'door' && (
                  <div>
                    <Label
                      htmlFor={`op-trim-${o.id}`}
                      className="text-xs text-muted-foreground"
                    >
                      Door trim
                    </Label>
                    <select
                      id={`op-trim-${o.id}`}
                      aria-label={`Opening ${i + 1} door trim`}
                      value={o.trim_action}
                      onChange={(e) =>
                        updateOpening(o.id, {
                          trim_action: e.target.value as OpeningTrimAction,
                        })
                      }
                      className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="replace">Replace trim only</option>
                      <option value="detach_reset">Detach & reset trim</option>
                      <option value="replace_prehung">
                        Replace prehung door (unit + trim)
                      </option>
                      <option value="none">None</option>
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>

          <Button type="submit" isLoading={loading} className="w-full">
            Build the scope
          </Button>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </form>

        <div>
          {result ? (
            <CalcResults result={result} />
          ) : (
            <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Enter the room and select Build the scope to see the drywall,
              texture, paint, baseboard, and trim line items with the math
              shown.
            </div>
          )}
        </div>
      </div>

      <section className="mt-14">
        <DimensionDivider label="Water claims" />
        <h2 className="mb-3 mt-5 font-ui text-2xl font-bold tracking-tight">
          Drywall flood cut repair, sized to the water line
        </h2>
        <div className="max-w-3xl space-y-3 text-[15px] leading-relaxed text-muted-foreground">
          <p>
            In a water loss you rarely tear out a whole wall. Wet drywall wicks
            up from the floor, so the standard repair is a flood cut: a straight
            horizontal cut across the wall above the water line, tear out the wet
            band below it, dry the cavity, and hang new board back to the cut. A
            2 foot flood cut is the common call for a shallow loss; a 4 foot cut
            is standard when the water stood higher or the wall stayed wet
            longer.
          </p>
          <p>
            Pick 2 ft or 4 ft and the calculator bills the tear-out per linear
            foot of affected wall and cuts the replacement board to the same
            height, instead of charging the full wall. The tear-out family
            follows the water category: a clean or gray loss uses the standard
            wet tear-out, a Cat 3 loss uses the contaminated-disposal variant,
            and an NFIP flood loss uses the no-bag family. Texture and paint
            still run the full wall so the repair blends and does not read as a
            2 foot stripe of fresh finish.
          </p>
        </div>
      </section>

      <AppPromo
        className="mt-14"
        context="In the app, room dimensions come from your sketch and these quantities become priced DRY and PNT line items automatically, with the water category and flood-cut height carried through."
      />

      <MethodSection title="How the scope is built">
        <p>
          Wall drywall starts from the perimeter of the room multiplied by the
          wall height, which gives the gross wall area. The tool then subtracts
          each door and window you mark as subtract from wall, because you do
          not hang board across an opening. If you include the ceiling, its area
          is the length times the width of the room. Texture and paint are
          figured on the full finished surface so the repair blends, and paint
          follows the number of coats you choose.
        </p>
        <p>
          Every line comes back as a real Xactimate item with a category and
          selector: remove board, hang board, texture, seal and paint,
          baseboard, and door trim each resolve to a verified code. When there
          is water damage the tear-out switches to the water items, a Cat 3 loss
          uses the contaminated-disposal variants, and an NFIP flood loss uses
          the no-bag family. A 2 or 4 foot flood cut bills the tear-out per
          linear foot and cuts the replacement board to the cut height. Because
          these are your Xactimate items, the tool never publishes pricing. It
          shows the quantities, the codes, and the action, and Xactimate prices
          them against your price list at import.
        </p>
      </MethodSection>

      <FaqSection items={FAQ_ITEMS} />

      <SourcesSection
        sources={[
          {
            name: 'Gypsum Association GA-216 application standards',
            detail:
              'Board, tape, and joint treatment coverage used to convert wall area into board and finish quantities.',
          },
          {
            name: 'IICRC S500 water damage tear-out conventions',
            detail:
              'Wet drywall tear-out, Cat 3 disposal, and NFIP no-bag flood cut families used when a water condition is selected.',
          },
          {
            name: 'Xactimate DRY, PNT, and FNC items (priced at import)',
            detail:
              'Every line resolves to a real Xactimate item. This tool outputs the codes and quantities; Xactimate prices them against your price list at import.',
          },
        ]}
      />
    </ToolPageShell>
  );
}
